<template>
  <el-dialog
    title="Please re-authenticate"
    width="30%"
    v-model="visible"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <el-row type="flex" align="middle" v-loading="isReauthenticating" element-loading-text='Reauthenticating...'>
      <el-col>
        <el-row v-if="getReauthStatus">
          Your session has expired. In order to proceed, you need to re-authenticate.
        </el-row>
        <el-row v-else>
          Please enter your credentials for the instance you are trying to access.
        </el-row>
        <el-row type="flex" v-if="showErrMsg" id="err-msg">
          Please provide all required credentials.
        </el-row>
        <el-row type="flex" align="middle">
          <el-form :model="form" ref="reauthForm">
            <el-form-item label="Username" style="margin-bottom: 0;">
              <el-input v-model="form.username" auto-complete="off"></el-input>
            </el-form-item>
            <el-form-item label="Password" style="margin-bottom: 0;">
              <el-input v-model="form.password" type="password" auto-complete="off"></el-input>
            </el-form-item>
          </el-form>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" align="middle" justify="center" style="padding-top: 12px;">
      <el-tag type="danger" v-if="error">{{ error }}</el-tag>
    </el-row>
    <template v-slot:footer><span class="dialog-footer">
      <el-button @click="cancel" class="reauth-btn" :disabled="isReauthenticating">
        Cancel
      </el-button>
      <el-button @click="submit" class="reauth-btn" :disabled="!hasCredentials() || isReauthenticating" type="primary">
        OK
      </el-button>
    </span></template>
  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { tabThruModalButtons, submitOnEnter } from '../utils/helpers/KeyNavHelper'

  export default {
    name: 'reAuthModal',
    props: {
      url: String
    },
    data () {
      return {
        form: {
          username: '',
          password: ''
        },
        formLabelWidth: '120px',
        error: null,
        bindEnterToSubmit: false,
        showErrMsg: false
      }
    },
    methods: {
      /**
       * Checks whether credentials have been provided
       */
      hasCredentials () {
        return !!(this.form.username && this.form.password)
      },
      /**
       * Resets the form
       */
      resetForm () {
        this.form = Object.assign({}, {
          username: '',
          password: ''
        })
      },
      /**
       * Cancels the reauthentication process. Resets the form, the closes the modal. If the user must
       * reauthenticate (e.g. due to an expired token), but hits 'Cancel', then they will be logged
       * out and returned to the login page.
       */
      async cancel () {
        try {
          this.resetForm()
          this.$store.dispatch('changeModalVisibility', { entityType: 'reauthModal', visible: false })
          if (this.getReauthStatus) {
            await this.$store.dispatch('logout')
            this.$router.push('/login')
          }
        } catch (e) {
          console.log(e)
        }
      },
      /**
       * Submits the reauthentication request, then closes the modal. If a follow-up route was specified,
       * (meaning where to navigate after reauthentication), then the user will be redirected to said route.
       */
      async submit () {
        this.showErrMsg = false
        if (this.hasCredentials()) {
          try {
            const reauthWasRequired = this.getReauthStatus
            const response = await this.$store.dispatch('reauthenticate', {
              request: { username: this.form.username, password: this.form.password },
              url: this.$props.url || this.activeInstance.url
            })
            this.resetForm()
            if (response.status === 0) {
              this.$message({ message: 'The instance URL you provided does not appear to be exist. Please check the URL and try again.', type: 'error', duration: 10000 })
              this.$store.dispatch('changeModalVisibility', { entityType: 'reauthModal', visible: false })
              return
            }
            if (reauthWasRequired) {
              this.$store.dispatch('changeModalVisibility', { entityType: 'reauthModal', visible: false })
              if (this.getPostAuthorizationRoute) {
                const destination = this.getPostAuthorizationRoute
                this.$store.dispatch('setPostAuthorizationRoute', null)
                if (!(this.$route.name.includes('New') || this.$route.name.includes('Edit')) && this.$route.name === destination) {
                  this.$router.go()
                } else {
                  this.$router.push({ name: destination })
                }
              }
            } else {
              this.$emit('reauthSuccess', this.$props.url)
            }
            return response
          } catch (e) {
            this.error = this.failure
            this.resetForm()
            setTimeout(() => {
              this.error = null
              this.$store.dispatch('resetReauthFailure')
            }, 10000)
          }
        } else {
          this.showErrMsg = true
        }
      }
    },
    computed: {
      ...mapGetters({
        getUser: 'getUser',
        getReauthStatus: 'getReauthStatus',
        isReauthenticating: 'isReauthenticating',
        getPostAuthorizationRoute: 'getPostAuthorizationRoute',
        failure: 'getReauthFailure',
        activeInstance: 'getActiveInstance'
      }),
      visible () {
        const visible = this.$store.getters.getModalVisibilityState
        if (visible.reauthModal && typeof visible.reauthModal === 'object' && visible.reauthModal.visible) {
          return visible.reauthModal.visible
        }
        return false
      }
    },
    watch: {
      getReauthStatus () {
        if (this.getReauthStatus) {
          this.form.username = this.getUser.username
          this.$store.dispatch('changeModalVisibility', { entityType: 'reauthModal', visible: true })
        } else {
          this.$store.dispatch('changeModalVisibility', { entityType: 'reauthModal', visible: false })
        }
      },
      visible () {
        if (this.visible) {
          tabThruModalButtons('reauth-btn')
        } else {
          tabThruModalButtons()
        }
      },
      bindEnterToSubmit () {
        if (this.bindEnterToSubmit && this.$refs.reauthForm.$el) {
          submitOnEnter(this.$refs.reauthForm.$el, this.submit)
        } else {
          submitOnEnter()
        }
      }
    },
    created () {
      this.form.username = this.getUser.username
    },
    updated () {
      /*
        In case someone is wondering why we set this.bindEnterToSubmit in
        Vue's updated hook and then watch it in order to use Mousetrap to
        bind the enter key to the submit function, it's because Element
        renders the content inside <el-dialog> *after* its "visible" prop is
        set to true, except for the buttons in the modal footer. So, trying to
        achieve the same thing within the 'visible' watcher (the way we bind tab
        to the modal footer buttons) won't work, because, at that point in time,
        the content within <el-dialog> isn't rendered in the DOM yet, so Mousetrap
        can't find the form to which we're binding the enter key. We could, of
        course, put the logic contained in the bindEnterToSubmit watchere right
        here, but then it would fire on every keystroke...
       */
      this.bindEnterToSubmit = !!this.visible
    }
  }
</script>

<style scoped>
  #err-msg {
    margin-top: 6px;
    color: red;
  }
</style>
